/**
 * Typed fetchers for /api/v1/ai/* — Azure AI Foundry model discovery, saved
 * chats, and the release risk assessment.
 *
 * Types come from `./_generated/schema` (CLAUDE.md §12.8 — generate, don't
 * hand-write), so a backend field rename is a `tsc` error here rather than a
 * runtime surprise. Regenerate with `pnpm gen:api` after any change to
 * `backend/src/daquma/api/v1/ai.py`.
 */

import type { components } from "./_generated/schema";
import { apiFetch } from "./apiFetch";

export type DeploymentOut = components["schemas"]["DeploymentOut"];
export type DeploymentListOut = components["schemas"]["DeploymentListOut"];
export type DiscoverDeploymentsIn = components["schemas"]["DiscoverDeploymentsIn"];
export type ChatOut = components["schemas"]["ChatOut"];
export type ChatListOut = components["schemas"]["ChatListOut"];
export type CreateChatIn = components["schemas"]["CreateChatIn"];
export type UpdateChatIn = components["schemas"]["UpdateChatIn"];
export type SetChatCollectionsIn = components["schemas"]["SetChatCollectionsIn"];
export type ChatCollectionOut = components["schemas"]["ChatCollectionOut"];
export type SetChatModelIn = components["schemas"]["SetChatModelIn"];
export type ChatModelCatalogOut = components["schemas"]["ChatModelCatalogOut"];
export type ChatConnectionOptionOut = components["schemas"]["ChatConnectionOptionOut"];
export type ChatModelOptionOut = components["schemas"]["ChatModelOptionOut"];
export type PlatformChatModelOut = components["schemas"]["PlatformChatModelOut"];
export type MessageOut = components["schemas"]["MessageOut"];
export type MessageListOut = components["schemas"]["MessageListOut"];
export type TurnOut = components["schemas"]["TurnOut"];
export type BlobFileRefIn = components["schemas"]["BlobFileRefIn"];
export type SaveReportIn = components["schemas"]["SaveReportIn"];
export type RunAssessmentIn = components["schemas"]["RunAssessmentIn"];
export type AssessmentOut = components["schemas"]["AssessmentOut"];
export type AssessmentListOut = components["schemas"]["AssessmentListOut"];

/** The shape of one entry inside `AssessmentOut.extraction` / `current_inputs`.
 *  Stays hand-written: the backend types those fields as `dict[str, Any]`, so
 *  OpenAPI carries no schema for them and there is nothing to generate. If the
 *  backend ever gives them a Pydantic model, delete this and alias it. */
export interface ExtractionSummary {
  container?: string;
  path?: string;
  filename?: string;
  format?: string;
  chars?: number;
  sha256?: string;
  /** Sheet names / page counts found. */
  parts?: string[];
  /** Sub-units that yielded no text (an empty or chart-only tab). Reported,
   *  never silently dropped. */
  skipped?: string[];
}

/** List an endpoint's model deployments. A data-plane read on the same endpoint
 *  and credential a completion uses — no subscription or resource group needed.
 *  Called before the connection is saved. */
export function discoverDeployments(
  token: string,
  input: DiscoverDeploymentsIn,
): Promise<DeploymentListOut> {
  return apiFetch<DeploymentListOut>("/api/v1/ai/foundry/deployments", {
    method: "POST",
    token,
    json: input,
  });
}

export function listChats(
  token: string,
  surface: "explore" | "support" | "user_admin" = "explore",
): Promise<ChatListOut> {
  return apiFetch<ChatListOut>(`/api/v1/ai/chats?surface=${surface}`, { token });
}

export function getChat(token: string, chatId: string): Promise<ChatOut> {
  return apiFetch<ChatOut>(`/api/v1/ai/chats/${encodeURIComponent(chatId)}`, { token });
}

export function createChat(token: string, input: CreateChatIn): Promise<ChatOut> {
  return apiFetch<ChatOut>("/api/v1/ai/chats", { method: "POST", token, json: input });
}

export function updateChat(token: string, chatId: string, input: UpdateChatIn): Promise<ChatOut> {
  return apiFetch<ChatOut>(`/api/v1/ai/chats/${encodeURIComponent(chatId)}`, {
    method: "PATCH",
    token,
    json: input,
  });
}

export function deleteChat(token: string, chatId: string): Promise<void> {
  return apiFetch<void>(`/api/v1/ai/chats/${encodeURIComponent(chatId)}`, {
    method: "DELETE",
    token,
  });
}

/** Replace the collections an Explore chat answers from (owner only). Audited
 *  server-side as `chat.indexes_changed`. */
export function setChatCollections(
  token: string,
  chatId: string,
  input: SetChatCollectionsIn,
): Promise<ChatOut> {
  return apiFetch<ChatOut>(`/api/v1/ai/chats/${encodeURIComponent(chatId)}/collections`, {
    method: "PUT",
    token,
    json: input,
  });
}

/** The Explore / Q&A model picklist: Azure AI Foundry connections the caller
 *  may use, each with the deployments it was configured with, plus the pinned
 *  platform deployment (`platform`, null when none is configured). */
export function listChatModels(token: string): Promise<ChatModelCatalogOut> {
  return apiFetch<ChatModelCatalogOut>("/api/v1/ai/chat-models", { token });
}

/** Point a chat at another connection + deployment (owner only). Audited
 *  server-side as `chat.model_changed`. */
export function setChatModel(
  token: string,
  chatId: string,
  input: SetChatModelIn,
): Promise<ChatOut> {
  return apiFetch<ChatOut>(`/api/v1/ai/chats/${encodeURIComponent(chatId)}/model`, {
    method: "PUT",
    token,
    json: input,
  });
}

/** The saved conversation, in order. History is durable server-side. */
export function listMessages(token: string, chatId: string): Promise<MessageListOut> {
  return apiFetch<MessageListOut>(`/api/v1/ai/chats/${encodeURIComponent(chatId)}/messages`, {
    token,
  });
}

/** Ask a question in a chat: answered only from its collections, with
 *  citations. Both turns are saved. */
export function sendMessage(token: string, chatId: string, content: string): Promise<TurnOut> {
  return apiFetch<TurnOut>(`/api/v1/ai/chats/${encodeURIComponent(chatId)}/messages`, {
    method: "POST",
    token,
    json: { content },
  });
}

// ---------------------------------------------------------------------------
// Veeva version risk assessment — a batch operation, not a conversation.
// ---------------------------------------------------------------------------

/** What extraction found in one input file — shown so the operator can confirm
 *  the file was read properly before trusting the report. */
export interface ExtractionSummary {
  container?: string;
  path?: string;
  filename?: string;
  format?: string;
  chars?: number;
  sha256?: string;
  /** Sheet names / page counts found. */
  parts?: string[];
  /** Sub-units that yielded no text (an empty or chart-only tab). Reported,
   *  never silently dropped. */
  skipped?: string[];
}

/** Run an assessment. Synchronous and can take tens of seconds — show progress. */
export function runAssessment(token: string, input: RunAssessmentIn): Promise<AssessmentOut> {
  return apiFetch<AssessmentOut>("/api/v1/ai/risk-assessments", {
    method: "POST",
    token,
    json: input,
  });
}

export function listAssessments(token: string): Promise<AssessmentListOut> {
  return apiFetch<AssessmentListOut>("/api/v1/ai/risk-assessments", { token });
}

export function getAssessment(token: string, id: string): Promise<AssessmentOut> {
  return apiFetch<AssessmentOut>(`/api/v1/ai/risk-assessments/${encodeURIComponent(id)}`, {
    token,
  });
}

/** Write the report to Blob as a .txt. Emits two audit events: `file.uploaded`
 *  for the storage write, and `risk_assessment.exported` for the fact that an
 *  AI-generated assessment left the platform. */
export function saveAssessmentToBlob(
  token: string,
  id: string,
  input: SaveReportIn,
): Promise<AssessmentOut> {
  return apiFetch<AssessmentOut>(
    `/api/v1/ai/risk-assessments/${encodeURIComponent(id)}/save-to-blob`,
    { method: "POST", token, json: input },
  );
}

/** Delete an assessment. A reason is mandatory — the row goes, so the audit
 *  event becomes the only account of why. */
export function deleteAssessment(token: string, id: string, reason: string): Promise<void> {
  const qs = `?reason=${encodeURIComponent(reason)}`;
  return apiFetch<void>(`/api/v1/ai/risk-assessments/${encodeURIComponent(id)}${qs}`, {
    method: "DELETE",
    token,
  });
}
